import { Link } from '@tanstack/react-router'
import { useAllEdges, useConcepts, useCourses, useLessons, useModules, useSessions } from '@/db/hooks'
import { PageTitle, SectionTitle } from '@/components/ui'
import { activeCourse } from '@/lib/learnDeck'
import { courseConcepts, deckHistory, knownOf, moduleProgress, outlineOf } from '@/lib/learn'
import { LearnTabs } from './parts'

const dayWord = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })

/** A hairline split into what is new, learning and known: the only learning state there is. */
const Split = ({ n, l, k }: { n: number; l: number; k: number }) => {
  const total = n + l + k || 1
  return (
    <span className="flex h-1 mt-2 rounded-full overflow-hidden bg-line" aria-hidden>
      <span className="bg-accent" style={{ width: `${(k / total) * 100}%` }} />
      <span className="bg-accent/45" style={{ width: `${(l / total) * 100}%` }} />
    </span>
  )
}

/**
 * What has been learned, and nothing about what has not: each module's concepts by familiarity, the
 * course's known count, and a quiet history of decks and how they went. No gaps, no missed days.
 */
export const ProgressView = () => {
  const courses = useCourses() ?? []
  const modules = useModules() ?? []
  const lessons = useLessons() ?? []
  const concepts = useConcepts() ?? []
  const sessions = useSessions() ?? []
  const edges = useAllEdges()
  const course = activeCourse(courses)
  const tree = course ? outlineOf(course.id, { modules, lessons, concepts, edges }) : []
  const { known, total } = knownOf(courseConcepts(tree))
  const history = deckHistory(sessions)
  const tests = deckHistory(sessions, 12, true)
  return (
    <div data-testid="learn-progress">
      <PageTitle>Progress</PageTitle>
      <LearnTabs />
      {course && (
        <>
          <div className="px-1 mt-2" data-testid="progress-known">
            <div className="text-[28px] leading-9 font-semibold">
              {known} of {total}
            </div>
            <div className="text-sm text-muted">patterns known in {course.spec.title}</div>
          </div>
          <SectionTitle>Modules</SectionTitle>
          <ul className="group-list" data-testid="progress-modules">
            {moduleProgress(tree).map((m) => (
              <li key={m.module.id} className="px-3.5 py-3" data-testid="progress-module">
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-[17px] font-medium">{m.module.spec.title}</span>
                  <span className="text-xs text-faint shrink-0">{m.total} patterns</span>
                </span>
                <span className="flex gap-4 mt-1 text-sm text-muted">
                  <span>{m.known} known</span>
                  <span>{m.learning} learning</span>
                  <span>{m.new} new</span>
                </span>
                <Split n={m.new} l={m.learning} k={m.known} />
              </li>
            ))}
          </ul>
        </>
      )}
      <SectionTitle>Decks</SectionTitle>
      {history.length === 0 ? (
        <p className="px-1 text-sm text-muted" data-testid="progress-none">
          Decks you finish show up here.
        </p>
      ) : (
        <ul className="group-list" data-testid="progress-history">
          {history.map((d) => (
            <li key={d.id} className="flex items-baseline justify-between gap-3 px-3.5 py-3 min-h-12">
              <span className="text-[15px]">{dayWord(d.at)}</span>
              <span className="text-sm text-muted text-right" data-testid="progress-deck">
                {[
                  d.right ? `${d.right} right` : '',
                  d.partial ? `${d.partial} partly` : '',
                  d.wrong ? `${d.wrong} to revisit` : '',
                ]
                  .filter(Boolean)
                  .join(', ') || 'looked through'}
              </span>
            </li>
          ))}
        </ul>
      )}
      {tests.length > 0 && (
        <>
          <SectionTitle>Tests</SectionTitle>
          <ul className="group-list" data-testid="progress-tests">
            {tests.map((d) => (
              <li key={d.id} className="flex items-baseline justify-between gap-3 px-3.5 py-3 min-h-12">
                <span className="text-[15px]">{dayWord(d.at)}</span>
                <span className="text-sm text-muted text-right" data-testid="progress-test">
                  {d.right} of {d.right + d.partial + d.wrong} right
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="mt-5 flex justify-center">
        <Link to="/learn" className="min-h-11 px-4 inline-flex items-center text-sm text-muted">
          Back to Learn
        </Link>
      </div>
    </div>
  )
}
